/**
 * NPC Briefings — client-side only.
 *
 * Keyed by campaign step index (0-based). Each entry defines the talking-head
 * NPC who briefs the player before the chapter starts. Shown once per chapter
 * (first tap only), then accessible via Codex replay.
 *
 * Briefings are multi-page dialogue. Each page is one tap-through. Party
 * members can interject with short reactions (speaker: 'party').
 *
 * Party lines use a variant system: an ordered list of conditions checked
 * against the active party. First match wins. Always include a default
 * (no condition) as the last variant.
 */

/** Condition for matching a party member to a dialogue variant. */
export interface PartyCondition {
  /** Match by strand template ID (e.g. 'cleric', 'emberdancer') */
  templateId?: string;
  /** Match by race (e.g. 'human', 'caligari', 'anthura') */
  race?: string;
  /** Match by class/archetype keyword in the unit class name */
  className?: string;
}

/** A single variant option for a party dialogue line. */
export interface PartyVariant {
  /** Condition to match against active party. Omit for the default/fallback variant. */
  condition?: PartyCondition;
  /** Override text for this variant */
  text: string;
}

export interface DialogueLine {
  /** Who speaks this line */
  speaker: 'npc' | 'party';
  /** The dialogue text. For party lines with variants, this is the default text. */
  text: string;
  /** Display name. Required for NPC lines. Party lines resolve from the matched strand. */
  name?: string;
  /** Title/role shown under name. Optional. */
  title?: string;
  /** Sprite key override for this speaker. */
  spriteKey?: string;
  /**
   * For party lines: ordered list of variants. First matching condition wins.
   * The matched strand's actual name is used as the speaker name.
   * If no variants match or none are provided, `text` is used with a generic speaker.
   */
  variants?: PartyVariant[];
  /** If set, this line is only shown when a party member matches the condition.
   *  Lines that fail the check are filtered out before dialogue starts. */
  requiredCondition?: PartyCondition;
  /** Override the voice cast key used for this line's voiceover. By default the
   *  voiceover pipeline keys NPC lines by `name`. Use this for mystery speakers
   *  ("???"), characters introduced before they have a name, or any line that
   *  should use a different voice than `name` would suggest. Use party voice
   *  keys (e.g. `party_anthura_female`) or named NPC keys (e.g. `Lens Aldric`). */
  voiceKey?: string;
}

export interface NpcBriefing {
  /** Default sprite key for the primary NPC of the scene. Stage rendering is gated
   *  by whether any line has `speaker: 'npc'` — so for party-only scenes (e.g. the
   *  party reacting on-site after a crawl), omit this field entirely. It still
   *  serves as a portrait fallback for party lines that can't resolve a unit, but
   *  that rarely fires in practice. */
  spriteKey?: string;
  /** Multi-page dialogue. Each entry is one page the player taps through. */
  dialogue: DialogueLine[];
}

export interface DecisionChoice {
  /** Backend identifier — 'kill' or 'spare' today, could expand for future forks. */
  id: 'kill' | 'spare';
  /** Primary label on the choice card. */
  label: string;
  /** Italic preview line below the label. */
  preview: string;
  /** Optional follow-up NPC line shown after click (typewriter), before animation fires. */
  followupLine?: string;
  /** Animation variant on the NPC sprite after click. */
  animation: 'execute' | 'spare';
}

export interface DecisionConfig {
  /** NPC cinematic sprite (e.g. kneeling / defeated pose). */
  spriteKey: string;
  /** NPC name displayed above dialog. */
  npcName: string;
  /** Optional title line under the name. */
  npcTitle?: string;
  /** Opening line shown as the scene fades in (typewriter). */
  openingLine: string;
  /** Two choice cards shown after the opening line completes. */
  choices: DecisionChoice[];
}

export interface ChapterBriefing {
  /** NPC briefing shown before party select */
  briefing: NpcBriefing;
  /** Debrief text shown on victory screen (game voice, unattributed italic) */
  debrief?: string;
  /** Full dialogue scene shown after crawl complete, over the combat backdrop */
  debriefDialogue?: NpcBriefing;
  /** Post-combat decision scene — runs BEFORE debriefDialogue if present.
   *  Player's click grants a strand server-side via /api/campaign/decision. */
  decision?: DecisionConfig;
  /** Hub background path relative to sprites/processed/ (e.g. 'bg/town_menu', 'hub_bg/irremar_v2').
   *  Falls back to 'bg/town_menu' if omitted. */
  hubBg?: string;
}

/**
 * Map of campaign step index -> chapter briefing data.
 * Steps without entries skip the briefing panel (go straight to crawls).
 */
export const CHAPTER_BRIEFINGS: Record<number, ChapterBriefing> = {
  // ── Act 1: The Flight ──────────────────────────────────────────────

  // Ch 1: Greycrag Mine — Aldric at Dawnfield, skarns in the foothills
  0: {
    hubBg: 'bg/town_menu',
    briefing: {
      spriteKey: 'npc_lens_aldric',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          title: 'Faith of the Sun, Dawnfield Chapel',
          text: "[low] Something has been digging in the hills west of town every night for a week. It is not raiders and it is not scavengers. Someone is doing methodical work up there, and my patrols come back lighter than they leave.",
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[tired] Skarn. They broke into old Granocen halls the wardens sealed off before I was posted here. I sent for the Tailor Guild the day the digging started. You are the first response I have received.",
        },
        {
          speaker: 'party',
          text: "What are they digging for?",
          variants: [
            { condition: { templateId: 'cleric' }, text: "What is in those halls worth losing patrols over, Lens?" },
            { condition: { race: 'granocen' }, text: "Those halls were sealed for a reason. What broke the seal?" },
            { condition: { race: 'anthura' }, text: "What is down there worth feeding the dark like that?" },
            { condition: { race: 'caligari' }, text: "A patrol that does not come back is a message. Whose?" },
            { condition: { race: 'vael' }, text: "A warden's seal does not break to scavengers. Who broke it for them?" },
            { condition: { race: 'thalassan' }, text: "What does a skarn pack want with a hall that has been quiet for a generation?" },
            { condition: { race: 'serath' }, text: "Patrols going missing under the same hill, twice. That is not coincidence." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[softly] [grim] I do not know. The wardens have not maintained those tunnels in a generation. Whatever brought the skarn here, my patrols did not survive long enough to learn it.",
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[stern] Clear the mine. Stop the digging, however it ends. I will not bury another patrol over a hole in the ground.",
        },
      ],
    },
    debrief: 'The skarn were organized. Tools, supplies, a crude camp inside the old halls. The patrols can return to their routes.',
    debriefDialogue: {
      spriteKey: 'anthura_druid',
      dialogue: [
        {
          speaker: 'party',
          text: "She was not here a breath ago. I would have heard her.",
          variants: [
            { condition: { race: 'anthura' }, text: "The air bent around her. Like a candle wick when a window opens. I felt it before I saw her." },
            { condition: { race: 'caligari' }, text: "She was not here. Then she was. There was no sound between, and that bothers me more than the appearing." },
            { condition: { race: 'granocen' }, text: "The dust did not move when she came. That is wrong." },
            { condition: { race: 'vael' }, text: "She was not here, and then she was. I have read about events like this. I have never stood next to one." },
            { condition: { race: 'thalassan' }, text: "Tide comes in faster than that, and even tide makes a sound." },
            { condition: { race: 'serath' }, text: "I have washed bodies that moved more air than she does." },
          ],
        },
        {
          speaker: 'npc',
          name: '???',
          spriteKey: 'anthura_druid',
          voiceKey: 'party_anthura_female',
          text: "Where is this? I remember warmth and roots and something pulling at me, and then I was here.",
        },
        {
          speaker: 'party',
          text: "She is Anthura. They walk out of the forest, they do not bloom out of stone.",
          variants: [
            { condition: { race: 'anthura' }, text: "She is one of us. And she is wrong-placed. We come out of trees, not rock." },
            { condition: { race: 'caligari' }, text: "Anthura. And the stories never said anything about appearing." },
            { condition: { race: 'granocen' }, text: "Anthura come out of trees. This is rock and dark. Someone changed the rules." },
            { condition: { race: 'vael' }, text: "She is Anthura. None of the books describe one appearing in the dark." },
            { condition: { race: 'thalassan' }, text: "I have only ever seen Anthura on coastal trade roads. Never one waking up underground." },
            { condition: { race: 'serath' }, text: "Anthura. And she has the look of someone who has just been born and does not know it yet." },
          ],
        },
        {
          speaker: 'npc',
          name: '???',
          spriteKey: 'anthura_druid',
          voiceKey: 'party_anthura_female',
          text: "What happened to me?",
        },
        {
          speaker: 'party',
          text: "Whatever happened to you, it did not happen alone. Come with us. We will work the rest of it out in the daylight.",
          variants: [
            { condition: { race: 'anthura' }, text: "Whatever happened to you, it happened to me too, somewhere. Walk with us. We are not leaving you down here." },
            { condition: { race: 'caligari' }, text: "I do not have a name for what just happened. You should not be alone with it. Come." },
            { condition: { race: 'granocen' }, text: "Your feet are under you. That is enough to start. Come." },
            { condition: { race: 'vael' }, text: "I do not know what happened to you. I do know you should not be here alone. Walk with us." },
            { condition: { race: 'thalassan' }, text: "You can walk. Good. We are losing the light, and this is not a place to spend the night." },
            { condition: { race: 'serath' }, text: "You stand and breathe and look at us. That is more than I can usually do for the ones I find. Come up." },
          ],
        },
      ],
    },
  },

  // Ch 2: The Escape — Aldric warns you, flee into Verdanmere
  1: {
    hubBg: 'bg/town_menu',
    briefing: {
      spriteKey: 'npc_lens_aldric',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          title: 'Faith of the Sun, Dawnfield Chapel',
          text: "[urgent] [whispers] Come closer, [short pause] and keep your voices down. We have less time than you think.",
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[low] A patrol rider was at the chapel before sunrise. He saw what you brought back. He files to the High Seat directly, and he was already remounted before I could get my robe on.",
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[grave] A Herald will be on the road by midday. By the Faith's own writ, [short pause] the Anthura are abomination, and a Herald is not sent to ask questions. If you raise a hand he will take you all down with her.",
        },
        {
          speaker: 'party',
          text: "Why warn us? You could have said nothing.",
          variants: [
            { condition: { templateId: 'cleric' }, text: "You wear the sun on your chest, Lens. Why are you telling us this?" },
            { condition: { race: 'anthura' }, text: "You called the Guild. Now you are telling us to run. Why both?" },
            { condition: { race: 'caligari' }, text: "A man who warns the people he is supposed to deliver up has a problem. What is yours?" },
            { condition: { race: 'granocen' }, text: "You sent for us. Now you send us out. Why both?" },
            { condition: { race: 'vael' }, text: "You are not asked to warn us, and yet you are warning us. Why?" },
            { condition: { race: 'thalassan' }, text: "You could have looked the other way. You did not. Why?" },
            { condition: { race: 'serath' }, text: "You are putting your neck under the writ to put ours past it. Why?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[softly] I did not mean for this to happen. I asked for help, and the Guild sent you. What is happening now is my doing as much as anyone's, and I am not going to be a man who watched.",
        },
        {
          speaker: 'npc',
          name: 'Lens Aldric',
          text: "[urgent] Go east. Into the deep Verdanmere, not the road that skirts it. The Concord does not patrol the trees, because the trees are not friendly. Push through to Hearthlands ground. Thornhaven is the first town on the other side. Do not stop until you are past the treeline.",
        },
      ],
    },
    debrief: 'The Verdanmere is dense and hostile near the border. The canopy swallows the sky. But the forest thins on the eastern side, and the land opens into rolling fields. Hearthlands territory. Thornhaven is close.',
    debriefDialogue: {
      spriteKey: 'anthura_druid',
      dialogue: [
        {
          speaker: 'party',
          text: "We are out. I had stopped counting on it.",
          variants: [
            { condition: { race: 'anthura' }, text: "Out. And the trees are not pulling at me anymore. That is the first thing I have noticed in two days." },
            { condition: { race: 'caligari' }, text: "Out. Nothing followed us through the last mile, which I am still not used to." },
            { condition: { race: 'granocen' }, text: "Open ground. Good." },
            { condition: { race: 'vael' }, text: "We are out. I would like to sit with that fact for a moment before anyone says anything else." },
            { condition: { race: 'thalassan' }, text: "Sky again. I had not realized how much I missed the weather of it." },
            { condition: { race: 'serath' }, text: "Sun on my face. I will take it for what it is." },
          ],
        },
        {
          speaker: 'party',
          text: "That forest knew me. I could feel it pulling, like something calling a name I do not have yet.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'party',
          text: "That was not a forest behaving like a forest. Something is awake in the Verdanmere, and it watched us leave.",
          variants: [
            { condition: { race: 'anthura' }, text: "The forest was not pushing us out. It was pushing us toward something. I cannot say what." },
            { condition: { race: 'caligari' }, text: "Forests do not push. That one pushed. We were not the first thing it has minded." },
            { condition: { race: 'granocen' }, text: "Old wood. Older than any I have stood under. Something in there is awake." },
            { condition: { race: 'vael' }, text: "I have walked old groves. None of them moved like that one. Something is awake in there." },
            { condition: { race: 'thalassan' }, text: "I have been in storms that paid us less attention than that forest did." },
            { condition: { race: 'serath' }, text: "Trees that watch you are trees that have learned to. Something taught those." },
          ],
        },
        {
          speaker: 'party',
          text: "Questions later. I want a wall and a hot meal before I think about any of this.",
          variants: [
            { condition: { race: 'anthura' }, text: "Walls first. The questions will still be here when I have eaten." },
            { condition: { race: 'caligari' }, text: "There is time to sit with this. Thornhaven first, the rest after." },
            { condition: { race: 'granocen' }, text: "Walk. Eat. Wonder later." },
            { condition: { race: 'vael' }, text: "I have a list of questions and none of them are for an empty road. Thornhaven first." },
            { condition: { race: 'thalassan' }, text: "Hot food and a roof. Then we name what just happened to us." },
            { condition: { race: 'serath' }, text: "Walls and a meal. Whatever followed us out of the trees can wait its turn." },
          ],
        },
      ],
    },
  },

  // Ch 3: Thornhaven — Hearthlands border town, river problem, Tailor duty beat
  2: {
    hubBg: 'hub_bg/thornhaven',
    briefing: {
      spriteKey: 'npc_steward_maren',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Steward Maren',
          title: 'Steward of Thornhaven',
          text: "[warm] The Guild has not sent Tailors through Thornhaven in three, maybe four years, and not many people come off the Verdanmere path in a hurry for the fun of it. Where are you headed?",
        },
        {
          speaker: 'party',
          text: "Irremar. We will not be in your town long enough to wear out the welcome.",
          variants: [
            { condition: { race: 'anthura' }, text: "Irremar. I have questions I cannot answer anywhere else." },
            { condition: { race: 'caligari' }, text: "Irremar. South road, three days, and we are gone before anyone notices we were here." },
            { condition: { race: 'granocen' }, text: "Irremar. We do not stop long." },
            { condition: { race: 'vael' }, text: "Irremar. Three days south, if the road still holds." },
            { condition: { race: 'thalassan' }, text: "Irremar. The road south, then off your books." },
            { condition: { race: 'serath' }, text: "Irremar. We will not crowd your gate any longer than the road requires." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Steward Maren',
          text: "[steady] Three days south, past the plateau. The road is good most of the way.",
        },
        {
          speaker: 'npc',
          name: 'Steward Maren',
          text: "[clears throat] Before you ride on, I will say this once and not ask twice. [thoughtful] Thornhaven has a problem the council in Galstead will not get to this month. [exhales sharply] If Tailors were passing through, Tailors could make a difference this week.",
        },
        {
          speaker: 'party',
          text: "Name the problem, Steward. We have walked further for less.",
          variants: [
            { condition: { race: 'anthura' }, text: "The forest pushed me out too. What is it doing to yours?" },
            { condition: { race: 'caligari' }, text: "Name what is wrong, and what you have already tried. We work better with the second part." },
            { condition: { race: 'granocen' }, text: "Tell us what is broken. We will look at it." },
            { condition: { race: 'vael' }, text: "If the council in Galstead is not coming, you should say what you would have asked them. We are listening." },
            { condition: { race: 'thalassan' }, text: "Name the problem, Steward. We have walked further for less." },
            { condition: { race: 'serath' }, text: "You said it once. Say it twice. We will work it." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Steward Maren',
          text: "[serious] The river has been rising for weeks. Things from the deep forest keep coming downstream that have no business being this far east. The riverbank is half a day north. Clear whatever is nesting there before the next flood takes the lower fields. That is all I will ask of you.",
        },
      ],
    },
    debrief: 'The river creatures were driven downstream by something deeper in the Verdanmere. The water is settling, but the forest is not.',
    debriefDialogue: {
      spriteKey: 'anthura_druid', // party conversation at the riverbank
      dialogue: [
        {
          speaker: 'party',
          text: "Driven, not lost. Whatever did the driving is still in the trees.",
          variants: [
            { condition: { race: 'anthura' }, text: "They were not running from hunger. Something upstream is herding the forest out of itself." },
            { condition: { race: 'caligari' }, text: "Nothing in the water is here for the water. Something further in pushed them down to us." },
            { condition: { race: 'granocen' }, text: "The river is doing the work. The thing pushing them in is not." },
            { condition: { race: 'vael' }, text: "They were driven, not displaced. The displacement is downstream of something else entirely." },
            { condition: { race: 'thalassan' }, text: "I have seen tide push schools onto a beach. This is the same shape. Something upriver is pushing." },
            { condition: { race: 'serath' }, text: "They came down the water already past dying. Something upstream is finishing them and missing the strays." },
          ],
        },
        {
          speaker: 'party',
          text: "I came from that forest. If it is sick, I want to know why.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'party',
          text: "First it pushed us out. Now it is pushing its own outward. Whatever is in there, it is not staying in there.",
          variants: [
            { condition: { race: 'anthura' }, text: "It did not want me. Now it does not want any of its own. The forest is bleeding into the country around it." },
            { condition: { race: 'caligari' }, text: "Two days ago it tried to eat us. Today it is throwing things at a town. Whatever is in there is escalating." },
            { condition: { race: 'granocen' }, text: "The forest is shedding. We will see worse before this is done." },
            { condition: { race: 'vael' }, text: "It expelled us. Now it is expelling its own. Whatever it is hosting, it is no longer holding it in." },
            { condition: { race: 'thalassan' }, text: "What did not want us now does not want anything alive in there. The Verdanmere is purging." },
            { condition: { race: 'serath' }, text: "It is not pushing things out anymore. It is dying onto the country around it." },
          ],
        },
      ],
    },
  },

  // Ch 4: The Long Horizon — desert plateau crossing, ancient ruins
  3: {
    hubBg: 'hub_bg/thornhaven',
    briefing: {
      spriteKey: 'npc_steward_maren',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Steward Maren',
          title: 'Steward of Thornhaven',
          text: "[steady] Irremar is three days on foot from here. Dry ground, exposed, and the old roads are not maintained past the last farm.",
        },
        {
          speaker: 'npc',
          name: 'Steward Maren',
          text: "[serious] [low] There are ruins out there, old ones that predate anything Irremar has catalogued. Most are unmapped. If you see stone markers with symbols you do not recognize, keep your guard up. The plateau has its own dangers.",
        },
        {
          speaker: 'party',
          text: "We have faced worse than old stones. Thank you, Steward.",
          variants: [
            { condition: { race: 'anthura' }, text: "Old stones with their own opinions. We will read them carefully. Thank you, Steward." },
            { condition: { race: 'caligari' }, text: "Unmapped is honest. We will manage. Thank you for the warning." },
            { condition: { race: 'granocen' }, text: "Old stone keeps its word. We will respect it." },
            { condition: { race: 'vael' }, text: "Pre-Irremar markers. I would like to see them, even if they would prefer otherwise. Thank you." },
            { condition: { race: 'thalassan' }, text: "We are good with bad weather and bad roads. Thank you for naming this one." },
            { condition: { race: 'serath' }, text: "Old guardians do not retire. We will go in expecting that. Thank you, Steward." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Steward Maren',
          text: "[warm] Irremar sits on the southern edge of the plateau. Follow the river south and you will find the road. Good luck, Tailors.",
        },
      ],
    },
    debrief: 'The plateau is clear. You passed through old ruins on the road, Vael stonework by the look of it, with carved markers the guardians were still protecting. You took a few of the smaller stones. Irremar is close.',
    debriefDialogue: {
      spriteKey: 'anthura_druid', // party conversation on the plateau
      dialogue: [
        {
          speaker: 'party',
          text: "Whatever they put in those stones to stand guard, it has been awake the whole time. None of it was tired.",
          variants: [
            { condition: { race: 'anthura' }, text: "Those stones were listening before we got there. They were still listening when we left." },
            { condition: { race: 'caligari' }, text: "I have fought things that needed sleep. Those did not. They were rested when we got there." },
            { condition: { race: 'granocen' }, text: "Old stone holds what it is told to hold. Those stones were told a long time ago." },
            { condition: { race: 'vael' }, text: "Vael work, but older than any masonry I was taught to read. There were marks on those stones I do not have a name for." },
            { condition: { race: 'thalassan' }, text: "Whatever was set in those stones has not run down. The maker's hand is still in the work." },
            { condition: { race: 'serath' }, text: "The stones were the keepers, and the keepers were not at rest. They were waiting." },
          ],
        },
        {
          speaker: 'party',
          text: "The carvings on those stones felt familiar. I do not know why.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'party',
          text: "Towers on the rim. That is Irremar, and we are already inside its sightline.",
          variants: [
            { condition: { race: 'anthura' }, text: "Towers. Walls. Lights I have never read at this distance. Irremar." },
            { condition: { race: 'caligari' }, text: "Irremar. Walls and a roof, and somebody else's problem to be the next thing past the gate." },
            { condition: { race: 'granocen' }, text: "Towers. Stone cut clean. We are seen." },
            { condition: { race: 'vael' }, text: "Irremar. I know that skyline from a book. Standing under it is another thing entirely." },
            { condition: { race: 'thalassan' }, text: "Towers on the rim. Inland city, but the lines are coastal. Someone designed it to be seen from far off." },
            { condition: { race: 'serath' }, text: "Towers, and we are already counted. Whatever Irremar is willing to give us, we have earned a roof for one night." },
          ],
        },
      ],
    },
  },

  // Ch 5: The Concord's Reach — Irremar, Concord templars followed you
  4: {
    hubBg: 'hub_bg/irremar_v2',
    briefing: {
      spriteKey: 'npc_scholar_aevryn',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          title: 'Vael Scholar-in-Residence, Irremar',
          text: "[softly] Steward Maren's word reached me ahead of you, and it did not exaggerate. She is young, she is Anthura, and I have been writing about the Emergence since the first of them walked out of the forest.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[thoughtful] I have a great many questions I would like to ask her. I would like to ask none of them today, because none of them is the most pressing thing in this room.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[grave] A Concord templar detachment crossed into Hearthlands territory at dawn, six riders under a Herald's seal. The Concord does not move Heralds across a sovereign border in those numbers unless something has alarmed them.",
        },
        {
          speaker: 'party',
          text: "They followed us across the border?",
          variants: [
            { condition: { templateId: 'cleric' }, text: "They crossed into sovereign territory? For us?" },
            { condition: { race: 'anthura' }, text: "Six riders, across a border, for me. Tell me you are sure." },
            { condition: { race: 'caligari' }, text: "Six riders, dawn, sovereign ground. That is not a doctrine errand." },
            { condition: { race: 'granocen' }, text: "Six templars over a line that is not theirs. That is not a small step." },
            { condition: { race: 'vael' }, text: "Six templars under a Herald's seal across a sovereign border is not a doctrine response. That is a state action." },
            { condition: { race: 'thalassan' }, text: "They came over the line at dawn? That is a navy maneuver, not a chapel one." },
            { condition: { race: 'serath' }, text: "Six templars across a border without warning. The Concord has stopped pretending." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[softly] Doctrine and faith might explain a Herald arriving at our walls with a writ. They do not explain six templars at dawn. What this looks like, when I read it carefully, [pause] is fear.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[steady] The council at Galstead has been notified, and the templars will not be permitted past the city walls. They are camped on the southern approach, however, and the Herald is not the kind of man who packs up because a city tells him to.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[thoughtful] While you handle them, I will be in the library. The Emergence is the largest puzzle of our generation, and I have correspondence from Kaeldavar that has never quite fit the frame. Today may give it one.",
        },
        {
          speaker: 'party',
          text: "Then we end the conversation ourselves.",
          variants: [
            { condition: { race: 'anthura' }, text: "If they will not turn back for a city, they will turn back for me. Let them see what they came for." },
            { condition: { race: 'caligari' }, text: "We did not run this far to hide behind walls." },
            { condition: { race: 'granocen' }, text: "They will not leave on their own. We move them." },
            { condition: { race: 'vael' }, text: "If they will not honor the border, they will honor the road. We meet them on it." },
            { condition: { race: 'thalassan' }, text: "Their captain is the wind. Cut the wind, the line goes slack." },
            { condition: { race: 'serath' }, text: "If they will not leave, we make the decision for them." },
          ],
        },
      ],
    },
    debrief: 'The templars are scattered. The southern road is clear. But the question remains: why would the Concord risk a diplomatic incident for one Anthura? Someone in the High Seat is afraid of what you might learn.',
    decision: {
      spriteKey: 'herald_orien_kneeling',
      npcName: 'Herald Orien of the Sixth',
      openingLine: "I rode here certain of everything. I am leaving certain of nothing.",
      choices: [
        {
          id: 'kill',
          label: 'Finish it.',
          preview: 'Cut him down. A dead Herald cannot report what he saw.',
          animation: 'execute',
        },
        {
          id: 'spare',
          label: 'Let him go.',
          preview: 'Spare him. A living witness may speak to others who will listen.',
          followupLine: "Thank you for showing the Sun's mercy. I will not come back for her.",
          animation: 'spare',
        },
      ],
    },
    debriefDialogue: {
      spriteKey: 'npc_scholar_aevryn',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          spriteKey: 'npc_scholar_aevryn',
          text: "[grave] You broke them. He crossed a sovereign border for one Anthura, and he was going to keep coming. The Concord does not spend Heralds on doctrine. They spend them on what they cannot afford to lose.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[thoughtful] While you were in the field, I was in the library. I have contacts in Kaeldavar. Months ago they sent me something I did not know what to do with. Now I think I do.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[steady] There is, I think, a likely connection between the dye and the Emergence. The Wells dye trade through Kaeldavar has surged since the year the first Anthura walked out of the Verdanmere. The Wells produce more dye now than at any point in their history, and Kaeldavar moves more of it than any port on the continent. I cannot tell you why the Concord hates her enough to spend a Herald. [short pause] I can tell you the dye is where I would look.",
        },
        {
          speaker: 'party',
          text: "We are past their reach for now, not for long. Tell us where the answer is.",
          variants: [
            { condition: { race: 'anthura' }, text: "Whatever they want with me, I want the name of it before they come again. Where do we look?" },
            { condition: { race: 'caligari' }, text: "They will be back, in a different shape. We learn what is under the surge before that." },
            { condition: { race: 'granocen' }, text: "They will come again. We need the answer before they do. Where?" },
            { condition: { race: 'vael' }, text: "If the dye trade is the lever, we should be the next hands on it. Where do we start, Aevryn?" },
            { condition: { race: 'thalassan' }, text: "If the dye is the line, we follow it back to the harbor it sails from. Where does that start?" },
            { condition: { race: 'serath' }, text: "Whatever they intend, we learn it before it reaches her. Where do we start?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[low] Go to Kaeldavar. There is a man in the Silent Market named Rhovane who watches what moves quietly through his city. Ask him who has been buying the surplus dye off the books, and where it is going. Find the buyer, and we find what the surge is for.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          text: "[steady] He owes me a favor. A Thalassan freighter leaves the harbor in two days for Kaeldavar. I will scribe his mark today; the Vael south-route runs faster than any freighter, and he will hear from me before you reach the harbor. Rest, equip yourselves, and be on the docks at second light.",
        },
      ],
    },
  },

  // ── Act 2: The Wake ──────────────────────────────────────────────
  // Hub: Kaeldavar (Caligari city in the Nightfold chasm, NE coast).
  // Voyage from Irremar is a narrative bridge (Aevryn's favor, Thalassan freighter,
  // one week at sea). Act 2 opens with the party already in the chasm.
  // NPC sprites npc_rhovane and npc_scholar_vesper generated 2026-04-23.
  // hubBg uses the existing hub_bg/kaeldavar.webp.

  // Ch 6: The Broker's Silence — Rhovane tests the party with a missing-contact errand
  //
  // TODO(decision-branching): when PartyCondition supports `decision: 'kill' | 'spare'`,
  // insert a new NPC line BEFORE the existing first Rhovane line, branching on the
  // Ch 5 Herald Orien decision:
  //   spared: "I have heard about the gate. You let him walk. The Concord is mortified, which I do not mind."
  //   killed: "I have heard about the gate. You cut down a Herald on Hearthlands ground. The Concord is mortified, and afraid, which I mind even less."
  // Lore: Rhovane's Caligari intel + merchant gossip travels faster than the party's
  // freighter. He has heard the news through his own network, not Aevryn's mark.
  5: {
    hubBg: 'hub_bg/kaeldavar',
    briefing: {
      spriteKey: 'npc_rhovane',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Rhovane',
          title: 'Silent Market, Kaeldavar',
          text: "A week at sea and you walk straight to my door. Aevryn's mark arrived three days ahead of you. I read it and did not reply, which makes this the conversation I was trying to avoid.",
        },
        {
          speaker: 'party',
          text: "Aevryn told us you would hear us out. He did not say what it would cost.",
          variants: [
            { condition: { race: 'anthura' }, text: "A week of salt under us, and your chasm has nothing of the woods I came from. We came anyway." },
            { condition: { race: 'caligari' }, text: "Aevryn said you would hear us. He did not name the price. We are listening for it." },
            { condition: { race: 'granocen' }, text: "Long road, deep stone. We are here. Speak." },
            { condition: { race: 'vael' }, text: "Aevryn's mark and a week at sea. Whatever you decide, we have already paid for the chair." },
            { condition: { race: 'thalassan' }, text: "We sailed a week on Aevryn's word. Tell us what the rest of it is going to cost." },
            { condition: { race: 'serath' }, text: "We did not sail a week for a warm welcome. Name your terms." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "My hearing is a service, not a courtesy. I charge in favors. Do something I need done today and we will find time to talk about whatever Aevryn thinks I can help you with.",
        },
        {
          speaker: 'party',
          text: "Name it.",
          variants: [
            { condition: { race: 'anthura' }, text: "Point us at the trail." },
            { condition: { race: 'caligari' }, text: "Name the favor. We will know whether to take it when you have." },
            { condition: { race: 'granocen' }, text: "Speak the work." },
            { condition: { race: 'vael' }, text: "Name it, and the shape it ends in. We work cleaner with both." },
            { condition: { race: 'thalassan' }, text: "Name the job. We will tell you what it costs." },
            { condition: { race: 'serath' }, text: "Name it. We will weigh it." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "A broker of mine went quiet five days ago. He missed two drops and a callback, and the last anyone saw of him was in the lower chasm. Find him. I want to know where he has been going, who he has been meeting, and what is in his hands when you catch up to him.",
        },
        {
          speaker: 'party',
          text: "Who else is looking for him?",
          variants: [
            { condition: { race: 'anthura' }, text: "What has been hunting him, and how long has it been on the trail?" },
            { condition: { race: 'caligari' }, text: "Who else is on him? And how quietly?" },
            { condition: { race: 'granocen' }, text: "Whose eyes are on him besides yours?" },
            { condition: { race: 'vael' }, text: "Who else has put hands toward him? And what have they touched on the way?" },
            { condition: { race: 'thalassan' }, text: "Who else has been pulling on his line?" },
            { condition: { race: 'serath' }, text: "Who else is hunting him?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "Whoever it is has been quiet about it, which tells me they are not amateurs. That is why you, and not one of mine. Bring him back. If he is not in a state to be brought, bring the work he has been touching. Do not announce yourselves.",
        },
      ],
    },
    debrief: "The broker was already dead when you found him, killed by someone who worked clean and kept it quiet. Another hand had been through his pockets since, but it had missed what was closed inside his fist: a small stoppered vial, no larger than a fingertip, the liquid inside it the color of wet violet. The glass was still warm.",
    debriefDialogue: {
      // No NPC in the scene — party is standing over the broker's body. Sprite omitted intentionally.
      dialogue: [
        {
          speaker: 'party',
          text: "Whoever killed him knew the work, and searched him afterward, and still missed what was in his hand.",
          variants: [
            { condition: { race: 'anthura' }, text: "The wound goes up under the ribs at a clean angle. Whoever pressed that blade has done it before, and they still missed what his fist was closed around." },
            { condition: { race: 'caligari' }, text: "Clean kill, professional search after. They knew exactly what to look for, and his hand was the one place they did not look." },
            { condition: { race: 'granocen' }, text: "Dead fast. Searched slow. They missed the small thing." },
            { condition: { race: 'vael' }, text: "The angle of the wound is trained. The search after was patient. The thing they missed was in plain sight, and that is the most interesting part." },
            { condition: { race: 'thalassan' }, text: "Clean strike, professional sweep, and still they missed the smallest thing on him. That is two skills meeting and only one of them passing." },
            { condition: { race: 'serath' }, text: "He was dead before he hit the floor, and someone went through him after. Whatever this is, they did not find it." },
          ],
        },
        {
          speaker: 'party',
          text: "The glass is warm. Not from being held. The thing itself feels like it has been somewhere warmer than a pocket.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'party',
          text: "Rhovane is going to want this in his hand before anyone else knows we took it.",
          variants: [
            { condition: { race: 'anthura' }, text: "This is warm and it is wrong, and Rhovane needs to hold it before the chasm hears we have it." },
            { condition: { race: 'caligari' }, text: "Back to Rhovane, by the lower stairs, before anyone misses what is no longer in his pocket." },
            { condition: { race: 'granocen' }, text: "Back to Rhovane. Now. Quiet." },
            { condition: { race: 'vael' }, text: "Rhovane should hold this himself before anyone else even hears the question of who held it last." },
            { condition: { race: 'thalassan' }, text: "We move it before the tide does. Rhovane gets it tonight." },
            { condition: { race: 'serath' }, text: "Rhovane should see this before the chasm wakes up." },
          ],
        },
      ],
    },
  },

  // Ch 7: The Duskmere Haul — Rhovane opens up, shipment heist on the underground lake
  6: {
    hubBg: 'hub_bg/kaeldavar',
    briefing: {
      spriteKey: 'npc_rhovane',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Rhovane',
          title: 'Silent Market, Kaeldavar',
          text: "Your broker's vial is genuine dye from the Violet Wells. The color is not one you can fake, and the warmth tells me it left the Wells inside the last week. That is real product, moving off-books through Kaeldavar, and my broker died getting close to it.",
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "Running a quiet operation against quiet people is not a thing you attempt unless you are very sure of yourself. I would like to know who is that sure of themselves.",
        },
        {
          speaker: 'party',
          text: "How do we find out?",
          variants: [
            { condition: { race: 'anthura' }, text: "Where do we pick up the trail?" },
            { condition: { race: 'caligari' }, text: "Where does the line start? We will follow it back." },
            { condition: { race: 'granocen' }, text: "Where do we begin?" },
            { condition: { race: 'vael' }, text: "Where do we start, and what should we already not be saying out loud?" },
            { condition: { race: 'thalassan' }, text: "Where on the water does this start? We follow that current." },
            { condition: { race: 'serath' }, text: "Where do we pick this up, and how quietly?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "Dye moves through the Duskmere by barge. Nobody pilots the run. The current does, and anyone sensible keeps off the water. The Silent Market has a rule about that lake. Small and quiet, or you do not come back. We have been letting the small shipments pass because the small ones obey the rule. Tonight a barge crosses that does not.",
        },
        {
          speaker: 'party',
          text: "Tell us what we are stealing.",
          variants: [
            { condition: { race: 'anthura' }, text: "Say the word and the shape of the crate. We will know it on the deck." },
            { condition: { race: 'caligari' }, text: "Name the crate. Name the mark. We will leave the rest of the deck alone." },
            { condition: { race: 'granocen' }, text: "Name the crate." },
            { condition: { race: 'vael' }, text: "Tell us what we are taking, and what we are pointedly not." },
            { condition: { race: 'thalassan' }, text: "Tell us what we are pulling off the barge." },
            { condition: { race: 'serath' }, text: "What are we taking?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "Take the crate, leave the barge, and do not sink it. Whoever ordered that shipment will know something happened, but they will not know what was taken, and they will not know who did it.",
        },
        {
          speaker: 'party',
          text: "And the crew?",
          variants: [
            { condition: { race: 'anthura' }, text: "What feet should we expect to find pressed into that deck?" },
            { condition: { race: 'caligari' }, text: "How many hands on the barge, and which ones are paid to be there?" },
            { condition: { race: 'granocen' }, text: "How many on the deck?" },
            { condition: { race: 'vael' }, text: "How many crew, and how many of them know what they are moving?" },
            { condition: { race: 'thalassan' }, text: "How many on the barge, and how many of them are watching the water and not the crate?" },
            { condition: { race: 'serath' }, text: "How many breathing, and how many of them are meant to keep breathing?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          text: "There is no crew. The current runs the barge. Whoever sent it paid for the route, not for men. The Silent Market does not put men on that water, and the ones who have been on it do not volunteer why. Do not disturb the water more than the work demands. Take what you came for and let the current take the rest.",
        },
      ],
    },
    debrief: "The crate is lead-sealed and heavier than it should be. You took it off a barge the water was already circling, and came back through the under-ways before the empty hull finished its run. Someone is already waiting for you in Rhovane's chambers. She brought her own tools.",
  },

  // Ch 8: The Reagent Trace — Vesper analyzes the crate, points at a Kaeldavar dealer, Malachar named
  7: {
    hubBg: 'hub_bg/kaeldavar',
    briefing: {
      spriteKey: 'npc_scholar_vesper',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          title: 'Dye-scholar, Violet Wells',
          spriteKey: 'npc_scholar_vesper',
          text: "This is dye, and the Wells produced it, start to finish. The product is not unusual. The volume is.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "A crate this size would last the Vigil a season, and it did not come through any of our books. Either the books are wrong, or someone routed it past them. I cannot answer either from here.",
        },
        {
          speaker: 'party',
          text: "My hand knows what this is for, even when my mouth does not.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "If this is moving through Kaeldavar, someone here is buying. Only one dealer in this chasm handles that kind of trade, and he has been stepping around my informants for a month.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "I need his ledger, not his body. The ledger will tell us who else is drinking from the same well.",
        },
        {
          speaker: 'party',
          text: "We get him talking.",
          variants: [
            { condition: { race: 'anthura' }, text: "He will talk. People always do once you stop letting them not." },
            { condition: { race: 'caligari' }, text: "I can have him on the ledger inside an hour. Quietly." },
            { condition: { race: 'granocen' }, text: "He will talk. We will wait." },
            { condition: { race: 'vael' }, text: "We get him talking, and we get the second name out before the first lie sets." },
            { condition: { race: 'thalassan' }, text: "We tighten the line until he does. He will." },
            { condition: { race: 'serath' }, text: "He will talk. They all want to, in the end." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "His warehouse is in the third tier. He keeps four thugs and a dye-runner at all times. Do not burn the place. The records are what we came for.",
        },
      ],
    },
    debrief: "The dealer talked. The ledger is incomplete, but it names a buyer who has been acquiring Wells dye in small but steady lots for two years, paying in old Vael coin. The name in the ledger is Malachar.",
    debriefDialogue: {
      spriteKey: 'npc_rhovane',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "Malachar. He has been at this longer than I thought.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "He is not good at it. If he were, we would not have a sample to read. That is the only reason he is alive.",
        },
        {
          speaker: 'party',
          text: "Something in the dye felt like a room does after someone has died in it.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "I had hoped this would be smaller. A warehouse to close, a cartel fight to referee, a ledger to burn. A man who pays in old Vael coin and accumulates Wells dye at this scale for two years has people behind him willing to wait that long, and that is the part I do not have a name for yet.",
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "Before Malachar, there is a tower. He worked out of it before the coast. My scouts found it two nights ago. He left it in a hurry, and what he left did not leave with him.",
        },
      ],
    },
  },

  // Ch 9: Malachar's Wake — the smaller tower Malachar used before the coast.
  // Vesper and Rhovane brief; the crawl reveals his earlier failed attempts.
  // No Concord-reveal here (saved for Ch 10 debrief). No Anthura prisoner. Visual horror
  // beat to make Ch 10 land with full weight.
  8: {
    hubBg: 'hub_bg/kaeldavar',
    briefing: {
      spriteKey: 'npc_rhovane',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Rhovane',
          title: 'The Quiet, Kaeldavar',
          text: "There is a tower in the hills above the coast road. Malachar worked out of it before he moved on. He left in a hurry. What he was making there did not leave with him.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "If he outgrew that tower and went to a larger one, what he did at the smaller was practice. Go and see the practice before you see the finish.",
        },
        {
          speaker: 'party',
          text: "What is there?",
          variants: [
            { condition: { race: 'anthura' }, text: "What did he leave behind that is still alive?" },
            { condition: { race: 'caligari' }, text: "What did your scouts see, before the one who came back stopped describing it?" },
            { condition: { race: 'granocen' }, text: "What is in the tower?" },
            { condition: { race: 'vael' }, text: "What did he leave, and what is the shape of it now?" },
            { condition: { race: 'thalassan' }, text: "What are we walking into?" },
            { condition: { race: 'serath' }, text: "What did he leave?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "My scouts did not stay to count. One returned, the other did not, and whatever is in that tower is not something their training prepared them for.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Bring back anything that is not moving. Leave anything that still is. I want to read his work before we meet him.",
        },
        {
          speaker: 'party',
          text: "Whatever he has made there will feel wrong to me. I will know before you do.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'party',
          text: "Any other hands on this?",
          variants: [
            { condition: { race: 'anthura' }, text: "Whose tracks lead in or out of that tower besides ours?" },
            { condition: { race: 'caligari' }, text: "Anyone else watching that tower right now? I would like that number to stay where it is." },
            { condition: { race: 'granocen' }, text: "Anyone else on this besides us?" },
            { condition: { race: 'vael' }, text: "Are there other hands on this work yet, or are we still the only ones who know?" },
            { condition: { race: 'thalassan' }, text: "Anyone else walking into that tower?" },
            { condition: { race: 'serath' }, text: "Other interested parties? Or are we still alone in the question?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "Not yet. We are the first to notice, and I would like to keep it that way. Clean the tower and do not announce yourselves coming or going.",
        },
      ],
    },
    debrief: "The tower was not empty. What was still in it was not whole. Malachar had worked there more than once, and none of it had come through cleanly. What had come through partway was what you fought. Vesper rode back in silence. Rhovane said only that the coast tomorrow would be worse.",
    debriefDialogue: {
      spriteKey: 'npc_scholar_vesper',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          title: 'Dye-scholar, Violet Wells',
          spriteKey: 'npc_scholar_vesper',
          text: "He has been tearing the membrane in that workshop for weeks. The one we just closed was the cleanest yet, but he never finished any of them.",
        },
        {
          speaker: 'party',
          text: "And the one we closed was the best he had managed.",
          variants: [
            { condition: { race: 'anthura' }, text: "And he left the lesser ones running. The room was full of half-shapes when we walked in." },
            { condition: { race: 'caligari' }, text: "And he walked away from the rest still in motion. Not finished. Not stopped." },
            { condition: { race: 'granocen' }, text: "He closed none of them. He left them open." },
            { condition: { race: 'vael' }, text: "He closed nothing on the way out. The tower is still working its way through what he started." },
            { condition: { race: 'thalassan' }, text: "And that was the one that worked, and the rest he left bleeding behind him." },
            { condition: { race: 'serath' }, text: "And we closed the one that came closest. The lesser dead are still in the walls of that tower." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Which is why he left. The tower was practice. He has gone where the ground will answer him, and he believes he can hold a tear open there.",
        },
        {
          speaker: 'party',
          text: "It was reaching for a shape. We cut it short of one.",
          variants: [
            { condition: { race: 'anthura' }, text: "The tear was reaching for a shape, and I felt it stop when we cut it." },
            { condition: { race: 'caligari' }, text: "It wanted a body. We took it before it had one." },
            { condition: { race: 'granocen' }, text: "It tried to take form. We did not let it." },
            { condition: { race: 'vael' }, text: "It was approaching a shape. We interrupted the approach. What it would have become, I cannot say." },
            { condition: { race: 'thalassan' }, text: "It was almost crossing. We cut it before it could." },
            { condition: { race: 'serath' }, text: "It was reaching for a body to wear. We closed the door on the hand." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "My scouts have had his road since he left the tower, and they are watching the coast tonight. By morning I will have a fix, and you will have your brief at first light. Whatever rest you can take, take it now, because I would rather we are the ones to interrupt him than the ones who arrive after.",
        },
      ],
    },
  },

  // Ch 10: The Underseam — Malachar fled his tower to a sea cave where the Nightfold
  // chasm runs out into open water. Crawl bg swaps from cave entry to the deeper inner
  // chamber.
  9: {
    hubBg: 'hub_bg/kaeldavar',
    briefing: {
      spriteKey: 'npc_rhovane',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Rhovane',
          title: 'The Quiet, Kaeldavar',
          text: "He is at the Underseam, the cliff-cave where the Nightfold chasm runs out into open water, and my scouts have had eyes on him all night. He is inside, and he is working.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "He chose it because the ground listens there. Whatever he asks gets a real answer. Working this crudely bleeds signal. I can feel it from the chasm. He will either hold it open or unmake himself before dawn.",
        },
        {
          speaker: 'party',
          text: "What is he trying to do?",
          variants: [
            { condition: { race: 'anthura' }, text: "What is he trying to bring through, Vesper?" },
            { condition: { race: 'caligari' }, text: "What is he asking for, exactly. And what answers a question like that." },
            { condition: { race: 'granocen' }, text: "What is he calling? Name it." },
            { condition: { race: 'vael' }, text: "What exactly is he attempting? You said it without saying it." },
            { condition: { race: 'thalassan' }, text: "What is he trying to drag through the door?" },
            { condition: { race: 'serath' }, text: "What is he trying to bring across? You can name it." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "He is forcing it. I cannot tell you what he is trying to bring through, only that he is bringing it crudely. Whatever answers him in that cave will not be a thing he can put a leash on.",
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "I want him alive, because his failure is worth more than his corpse, and I want him sitting across from me when he is ready to talk.",
        },
        {
          speaker: 'party',
          text: "We go now.",
          variants: [
            { condition: { race: 'anthura' }, text: "Now. Before whatever he is calling answers him." },
            { condition: { race: 'caligari' }, text: "We leave now. Quietly down, loud once we are inside." },
            { condition: { race: 'granocen' }, text: "We go." },
            { condition: { race: 'vael' }, text: "Now is the right window. Later is not a window at all." },
            { condition: { race: 'thalassan' }, text: "Tide is right. We go." },
            { condition: { race: 'serath' }, text: "Now. Before he gets the answer he is asking for." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "The way down to the Underseam is three hours if you move, and the path is not marked, so do not stop for sunset. If he holds it open long enough, what answers him becomes our problem. Vesper cannot tell me what it will be, and she does not like that she cannot tell me.",
        },
      ],
    },
    debrief: 'The tear closed when you cut the channels. Light collapsed inward, then out, then nothing. Malachar was on his knees in the cave-mouth, whispering the same word in a language no one alive speaks. He stopped when you took his hands. He has not spoken since.',
    debriefDialogue: {
      spriteKey: 'npc_rhovane',
      dialogue: [
        {
          speaker: 'party',
          text: "He has not said a word since the ruin.",
          variants: [
            { condition: { race: 'anthura' }, text: "He has not made a sound since we put him on his knees. He is not in shock. He is listening to something." },
            { condition: { race: 'caligari' }, text: "He has been silent since the ruin. Watch him. He is choosing it." },
            { condition: { race: 'granocen' }, text: "He has not spoken. Not once." },
            { condition: { race: 'vael' }, text: "He has not spoken since the cut. He is composing something, not recovering from anything." },
            { condition: { race: 'thalassan' }, text: "He has been quiet since the cave. Quiet on purpose, by the look of him." },
            { condition: { race: 'serath' }, text: "He has not spoken since we took him. He looks like a man waiting for permission." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "He will. They all do. Vesper will read what he is not saying, and I will wait for what he is.",
        },
        {
          speaker: 'party',
          text: "When the air opened, something looked back at me. It knew my shape. Not my name. My shape.",
          requiredCondition: { race: 'anthura' },
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "What he was doing was not asking for power. It was asking a question. He expected an answer. He was going to get one, and he was wrong about what that would mean.",
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "His papers are inside. I have had an hour with them. Malachar was Concord, in a manner of speaking, senior enough to know what the Dawn Watch does and exiled or fled or both. His old colleagues have been sending him materials. Tonight was funded by men who still wear the sun.",
        },
        {
          speaker: 'party',
          text: "Why would they fund this?",
          variants: [
            { condition: { race: 'anthura' }, text: "Why him? Why this work, in a cave, in the dark, with a man they had already pushed out?" },
            { condition: { race: 'caligari' }, text: "Why fund the embarrassment? They had already disowned him. Sending materials after that is a different ledger." },
            { condition: { race: 'granocen' }, text: "Why him?" },
            { condition: { race: 'vael' }, text: "Why bankroll the man they exiled? That is not contrition. That is a hand on the puppet from the same room." },
            { condition: { race: 'thalassan' }, text: "Why pay an exile? An exile is a payment they are still afraid to stop making." },
            { condition: { race: 'serath' }, text: "Why would they?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "I do not know yet. He will tell me. When he does, I will send word.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "That is your question to chase. I have mine. The dye he used did not come from the Wells through honest channels. Someone at my home is diverting product, or my home is already compromised. I cannot answer either from here.",
        },
        {
          speaker: 'npc',
          name: 'Rhovane',
          spriteKey: 'npc_rhovane',
          text: "Go with her. Lys Vara first, then the Wells. Malachar stays with me until he breaks.",
        },
        {
          speaker: 'party',
          text: "Home with you, then.",
          variants: [
            { condition: { race: 'anthura' }, text: "We go where the dye goes. Home with you." },
            { condition: { race: 'caligari' }, text: "South, then. Off the maps Rhovane does not own." },
            { condition: { race: 'granocen' }, text: "We walk with you." },
            { condition: { race: 'vael' }, text: "South with you. The Wells are the next room of the same question." },
            { condition: { race: 'thalassan' }, text: "South. Off the books and on the next tide that does not have eyes." },
            { condition: { race: 'serath' }, text: "Home with you, then. Quietly." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Home with me. Not from this harbor. We ride south to Volthalar overland, and there I know a Thalassan captain who keeps his ledgers off Concord rolls. I do not want eyes on us until I know whose eyes those are.",
        },
      ],
    },
  },

  // ── Act 3: The Long Vigil ──────────────────────────────────────────

  // Ch 11: The Drowned Reach — Volthalar hub. The party hires a Thalassan
  // captain to take them south, and he will not sail until a coastal
  // village two days down the coast has been settled. They walk it.
  10: {
    hubBg: 'hub_bg/thalassan_harbor',
    briefing: {
      spriteKey: 'npc_scholar_vesper',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Volthalar. The captain I named in Kaeldavar has his ship at the dock. He will run us south to Lys Vara once a village two days down the coast goes quiet. He passed it sailing north last week. The drowned still stand on its piers, and he will not put his hull back on that water until it stops being wrong.",
        },
        {
          speaker: 'party',
          text: "How long?",
          variants: [
            { condition: { race: 'anthura' }, text: "How long has it stood wrong?" },
            { condition: { race: 'caligari' }, text: "How long, and who else knows?" },
            { condition: { race: 'granocen' }, text: "How long?" },
            { condition: { race: 'vael' }, text: "How long has it stood? You name it carefully, which tells me longer than a week." },
            { condition: { race: 'thalassan' }, text: "I have seen the drowned stand. Never for long. How long?" },
            { condition: { race: 'serath' }, text: "How long? That is rite-work that should not have lasted." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Seven days by his count, which puts it the week we found Malachar's ruin. The water has been wrong all the way down from Kaeldavar. I waited for the evidence. The captain has handed it to me.",
        },
        {
          speaker: 'party',
          text: "Then we settle it.",
          variants: [
            { condition: { race: 'anthura' }, text: "Then we settle it before the tide. We walk first." },
            { condition: { race: 'caligari' }, text: "He will not move his ship until we move his problem. Fair." },
            { condition: { race: 'granocen' }, text: "Settle first. Sail after." },
            { condition: { race: 'vael' }, text: "He has named the price of his passage. We pay it." },
            { condition: { race: 'thalassan' }, text: "Captain's right. Tell us where to step on, we step on." },
            { condition: { race: 'serath' }, text: "We will walk it. We have walked worse." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Settle the harbor. He sails. The coast on foot is a month, and we do not have a month.",
        },
      ],
    },
    debrief: 'The piers stilled. Whatever had held the drowned upright let them down, and the village fell back into the shape villages keep when they are done becoming something else. The captain took the news on the quay, called for the tide, and the ship was off the cleats before his ink dried. Lys Vara is three days south on this wind.',
  },

  // Ch 12: The Burning Fen — First Vigil meeting. Elder Thelys asks the
  // party to go after her keepers missing in the Palemire fen-fires.
  11: {
    hubBg: 'hub_bg/lys_vara_v2',
    briefing: {
      spriteKey: 'npc_elder_thelys',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          title: 'Vigil Elder',
          text: "Your captain's crew told my keepers you settled the Drowned Reach before you docked, and you have my thanks for that. It was work the order had been leaving.",
        },
        {
          speaker: 'party',
          text: "We were passing through.",
          variants: [
            { condition: { race: 'anthura' }, text: "It was on the way. The work was waiting, and we took it." },
            { condition: { race: 'caligari' }, text: "We were passing through, and the captain made it cheaper than the alternative." },
            { condition: { race: 'granocen' }, text: "The work was on our road. We walked it." },
            { condition: { race: 'vael' }, text: "The harbor needed walking, so we walked it. The captain made the case persuasively." },
            { condition: { race: 'thalassan' }, text: "Our captain would not sail past it. We made the harbor pass." },
            { condition: { race: 'serath' }, text: "The keepers had left work undone. We finished it." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "The Palemire fen has been burning for a week. The storm set off the marsh-gas, and the fires will not go out. I sent funerary keepers to the border hamlets four days ago, and nothing has come back.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "This is not marsh-gas. The smoke smells of dye, and I do not know which one. Something is leaking under the fen and the storm is venting it.",
        },
        {
          speaker: 'party',
          text: "You want them found.",
          variants: [
            { condition: { race: 'anthura' }, text: "Four days. In a fen that is burning the wrong color. You know what you are asking." },
            { condition: { race: 'caligari' }, text: "You want them found. The number you do not say out loud is the number that scares you." },
            { condition: { race: 'granocen' }, text: "You want them brought back. We will." },
            { condition: { race: 'vael' }, text: "Four days in that fen. You know what you are asking, and you are asking it anyway." },
            { condition: { race: 'thalassan' }, text: "Four days under burning gas. You know we will not find all of them." },
            { condition: { race: 'serath' }, text: "Your keepers, and you want them home. Whatever home looks like for them now." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "Bring them home. If some are past bringing, bring what you can. The Vigil is a small order and we do not leave our own in smoke.",
        },
      ],
    },
    debrief: 'Three keepers walked out with you. One was carried. Elder Thelys counted her people at the Vigil gate and did not need to be told what the fen had been doing. She said nothing the party did not already know.',
  },

  // Ch 13: The Violet Road — Malachar's old smuggling wing tipped a
  // dye cart on the ridge road. The keepers who were unloading it got
  // soaked. They are still becoming whatever the dye is making of them.
  // Chapter thesis: the order has rules for handling dye in volume,
  // they are not ceremonial, and this is what they exist to prevent.
  12: {
    hubBg: 'hub_bg/lys_vara_v2',
    briefing: {
      spriteKey: 'npc_elder_thelys',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          title: 'Vigil Elder',
          text: "A Wells keeper died two nights ago on the road north, alone, defending a shipment that was not one of ours.",
        },
        {
          speaker: 'party',
          text: "Whose was it?",
          variants: [
            { condition: { race: 'anthura' }, text: "Whose, then? Whose road, whose dye, whose hands at the cart?" },
            { condition: { race: 'caligari' }, text: "Wells dye on a Pale Marsh road, in volume. Someone is moving without the order knowing." },
            { condition: { race: 'granocen' }, text: "Whose cart?" },
            { condition: { race: 'vael' }, text: "Whose was the cart, and whose was the keeper standing in front of it?" },
            { condition: { race: 'thalassan' }, text: "Whose dye, and whose road. Both questions matter." },
            { condition: { race: 'serath' }, text: "He died for dye that was not ours." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "Serath, but not ours. They left the order eight years ago to follow a man named Malachar. They have been moving dye for him out of the Wells in volumes the order never authorized, and they are not trained to move it. Two nights ago that caught up with them. A cart went over on the ridge road. The barrels did not hold.",
        },
        {
          speaker: 'party',
          text: "All of it on the ground, then.",
          variants: [
            { condition: { race: 'anthura' }, text: "On them?" },
            { condition: { race: 'caligari' }, text: "On them, I assume." },
            { condition: { race: 'granocen' }, text: "Soaked through, then." },
            { condition: { race: 'vael' }, text: "On the ground, on the men. Both, I would expect." },
            { condition: { race: 'thalassan' }, text: "How much of it on the ground?" },
            { condition: { race: 'serath' }, text: "Soaked, then. Standing in it. The order has rules for that for a reason." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "On them. The ones unloading the cart were standing in the dye when it pooled, and the dye does what it does to a body that drinks it through the skin. Some of them ran, but they did not run far, and the ones still walking are not done becoming. The order has rules for handling dye in volume that are not ceremonial. They are why this does not happen to us.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "The Kaeldavar dealer's ledger gave us two years. The cart's manifest gives us eight. Six holding stations, three couriers, a chain running quietly for almost a decade. That is a system, not a man.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Concord. Probably the Dawn Watch. They buy our dye openly, but never at this volume. Some sites on the manifest are not Pale Marsh, and I want to know what is at them. I am guessing. I want to be wrong.",
        },
        {
          speaker: 'party',
          text: "And now we follow it up the road.",
          variants: [
            { condition: { race: 'anthura' }, text: "Then we go up the road and meet whatever the dye is making of them." },
            { condition: { race: 'caligari' }, text: "And now we walk up that ridge with our eyes open." },
            { condition: { race: 'granocen' }, text: "Then we walk." },
            { condition: { race: 'vael' }, text: "Then we follow the manifest up the road, station by station, and we see where the chain goes silent." },
            { condition: { race: 'thalassan' }, text: "Where is the cart, exactly? We start there." },
            { condition: { race: 'serath' }, text: "Up the road, then. We will bring the keepers home, however much of them comes." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "I want them caught. I want what they were carrying, and I want the names and the route and the hand at the other end of it. Bring me one alive who can still talk, if there is still a man left inside the thing the dye is making. Malachar is in a cell in Kaeldavar. His chain is not. We pull on it from this end.",
        },
      ],
    },
    debrief: "The ridge above the road is what the Vigil will not walk for a generation. The cart is still where it fell; the verge around it does not look like a verge anymore. Velara Mourn went into the dye and did not come out, and the rest are not finished going. The cart's manifest pointed to a name the order will read hard tomorrow. Elder Thelys listened to the report through, said nothing for a long time, and then asked you to come up to the Wells with her. She has a different problem there, and it is not the splinter's.",
  },

  // Ch 14: The Stilled Well. A Well has gone quiet, and the marsh around it
  // has stopped being held quiet. The party walks the site investigating what
  // woke. Discovery → handoff to Ch 15 (the Vigil) for the deeper answer.
  // The "permission has run out" thread is planted here but not paid off.
  13: {
    hubBg: 'hub_bg/lys_vara_v2',
    briefing: {
      spriteKey: 'npc_elder_thelys',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          title: 'Vigil Elder',
          text: "The Stilled Well, in the records, though we have not had to use that name in my lifetime. Now we do. A Well that has drawn back, not drained. The keepers do not have language for what the marsh around it has begun doing.",
        },
        {
          speaker: 'party',
          text: "How long?",
          variants: [
            { condition: { race: 'anthura' }, text: "How long has the bog been wrong?" },
            { condition: { race: 'caligari' }, text: "Months, or longer than that?" },
            { condition: { race: 'granocen' }, text: "How long has the Well been pulling back?" },
            { condition: { race: 'vael' }, text: "How long has the change been measurable?" },
            { condition: { race: 'thalassan' }, text: "How long has the water been doing this?" },
            { condition: { race: 'serath' }, text: "How long has the Well been receding, Elder?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "Months at the surface, years before that. The order has known for as long as the order has existed that the Wells are not the only thing drinking from this water. We have always taken less than we could. We called it discipline. The truer word is permission, and the permission has run out.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Eight years. Malachar's bulk pull started eight years ago. The Anthura emerged inside that window. The Wells started slipping inside that window. I am not making the connection. The dates are making it for me.",
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "I have been in this order long enough that I should have put those dates together myself. I did not. We are catching up to a fire we let burn.",
        },
        {
          speaker: 'party',
          text: "What is happening at the site now?",
          variants: [
            { condition: { race: 'anthura' }, text: "And the marsh, then? What is it doing now that the Well is not holding it?" },
            { condition: { race: 'caligari' }, text: "What did your keepers see at the site, before they stopped sending word?" },
            { condition: { race: 'granocen' }, text: "What does the ground do?" },
            { condition: { race: 'vael' }, text: "What is at the site now? You are not telling me, which tells me you have not been told either." },
            { condition: { race: 'thalassan' }, text: "What is the water doing at the site? Has it gone tide-wrong, or is it stranger than that?" },
            { condition: { race: 'serath' }, text: "What is the marsh doing at the site, Elder? The keepers' words, not yours." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "Wolves down where wolves do not come. The wildlife will not settle. The keepers say the bog itself is not still. Walk it. I want to know what woke at the Well, and I want to know it before whatever it is decides where to go next.",
        },
      ],
    },
    debrief: "The Well was empty, and the bog around it was not. Stone stood up. Mire stood up. The old cypresses the order had walked past for generations stood up. At the centre, fused into the chamber floor, something the keepers have no language for at all. The party brought it down. They could not name it. Vesper said quietly that the answer is not at the surface; the answer is at the Vigil, in the chamber under the chambers, where the records run deeper. Elder Thelys nodded once and started packing.",
  },

  // Ch 15: The Long Vigil — Pest-control rite gone wrong. Zythax has been
  // drinking the Wells from below for two years. Order has been culling his
  // spawn for centuries without understanding what they were keeping
  // dormant. Party fights through escalating spawn, kills Zythax, finds the
  // Soulloom in the chamber behind him. Thelys: ancestral knowledge lost.
  // Vesper: takes the documents to Aevryn at Irremar. Soulloom unlocks.
  14: {
    hubBg: 'hub_bg/the_long_vigil',
    briefing: {
      spriteKey: 'npc_elder_thelys',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          title: 'Vigil Elder',
          text: "The order rides up to the Vigil twice a generation, and the records call it a rite though the keepers know it as a cull. We clear what has hatched, we close the door, we ride home. I was not due back for six more years. I am back now because the keepers I sent ahead came home wrong.",
        },
        {
          speaker: 'party',
          text: "What did they say?",
          variants: [
            { condition: { race: 'anthura' }, text: "What did they bring back? Their words, not yours." },
            { condition: { race: 'caligari' }, text: "What did your keepers see, before they stopped describing it?" },
            { condition: { race: 'granocen' }, text: "What did they bring back?" },
            { condition: { race: 'vael' }, text: "What did the keepers see, in their own language? I would like the original phrasing if you have it." },
            { condition: { race: 'thalassan' }, text: "What did they see down there, Elder? The first words, not the steady ones." },
            { condition: { race: 'serath' }, text: "What did they see down there?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "Numbers and sizes the records do not describe. Our ledger says we should find perhaps twenty hatchlings per cull. The keepers stopped counting past two hundred. The mother of those things is still down there, and the mother is not in the records at all, because the order has never seen her.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "The Wells have been slipping for months. The fen smelled of dye last week. The Reach was wrong before that. Eight years of bulk extraction adds up, and whatever has been chewing on this from below is heavier than it was. We are walking into the part the order never reaches.",
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "The order knows the rite. The order does not know the reason. The keepers who knew the reason are long dead, and they wrote enough down to keep the rite running and not enough to explain why it works. We will be writing for them tonight.",
        },
        {
          speaker: 'party',
          text: "Then we walk.",
          variants: [
            { condition: { race: 'anthura' }, text: "Then we walk. Whatever the order has been forgetting, we will find before we leave." },
            { condition: { race: 'caligari' }, text: "Then we go. Quietly down, and we read what is left of the records after." },
            { condition: { race: 'granocen' }, text: "Then we go." },
            { condition: { race: 'vael' }, text: "Then we read what they wrote, after. The rite first, the ledger second." },
            { condition: { race: 'thalassan' }, text: "Then we walk, and the order can copy our notes when we are done." },
            { condition: { race: 'serath' }, text: "Then we walk, and we read after." },
          ],
        },
      ],
    },
    debrief: 'The chamber went quiet when Zythax fell. The chamber is very large, and it is older than the rite that has been guarding it. Behind the body, where no keeper had walked in living memory, there is a shape the order does not have a word for.',
    debriefDialogue: {
      spriteKey: 'npc_elder_thelys',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          title: 'Vigil Elder',
          text: "That is not in the ledger. I know the ledger by heart, and that is not in the ledger.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "It is loom work, old loom work. The frame is not Vigil. This is older than the order.",
        },
        {
          speaker: 'party',
          text: "What is it?",
          variants: [
            { condition: { race: 'anthura' }, text: "What is this, Vesper? It is humming and I do not know in what." },
            { condition: { race: 'caligari' }, text: "What is it. It is not a tool I recognise, and I have catalogued more rooms than this." },
            { condition: { race: 'granocen' }, text: "Name it." },
            { condition: { race: 'vael' }, text: "It is a loom that does not weave thread. What does it weave?" },
            { condition: { race: 'thalassan' }, text: "What are we looking at, Vesper? It is older than the rite that hid it." },
            { condition: { race: 'serath' }, text: "What is it, Vesper? You are the only one in the room with a guess." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "The order has been guarding this without knowing. The Tailor records I have read once or twice in my life call it a Soulloom. I have never seen one. I do not think anyone alive has.",
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "My predecessors knew the rite. I am willing to wager they did not know what was behind it. The order has been a curator that did not read the placards.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "I am taking the inscriptions and the manifests to Aevryn at Irremar. He has the records I do not. I have read enough to know I cannot read the rest. He can.",
        },
        {
          speaker: 'npc',
          name: 'Elder Thelys',
          text: "Go. The Vigil has its own work tonight. I am calling the keepers home, and the order will be remade around what it has actually been guarding. We will be worth it this time.",
        },
      ],
    },
  },

  // ── Act 4: The Reading ─────────────────────────────────────────────

  // Ch 16: The Reading at Irremar. Aevryn has decoded two more sites on the
  // Vigil inscriptions list: the western end of the Long Horizon (Dawn Watch)
  // and a site in Verdanmere at the base of an ancient tree (Motherroot,
  // where the Anthura emerged). Aevryn does NOT name the Dawn Watch as a
  // weapon at this chapter. The weapon word lands at Ch 18 with Aldric.
  // Aevryn here plants the Anthura-Concord coincidence theory and commits
  // to the convoy because he wants to figure it out on site.
  //
  // Crawl: Sun-Sworn operatives reach the archive to silence the reading.
  // Crawl-hook ending TODO: the last 1-2 dialogue beats pivoting into the
  // crawl are pending a design pick (one-hour timer, colleague gone quiet,
  // city bell, direct enemy ID, or combination).
  15: {
    hubBg: 'hub_bg/irremar_archive',
    briefing: {
      spriteKey: 'npc_scholar_aevryn',
      dialogue: [
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          title: 'Scholar of Irremar',
          text: "I have studied what Vesper carried out of the chamber for three days and three nights, and the longer I sit with it, the less I am willing to put on paper. I owe you an apology and a long explanation, in that order.",
        },
        {
          speaker: 'party',
          text: "Skip the apology. The explanation is the part we walked back for.",
          variants: [
            { condition: { race: 'anthura' }, text: "The room has been heavy since we walked in, and your face is the heaviest part of it. Say what you have." },
            { condition: { race: 'caligari' }, text: "Apology noted. The explanation is the conversation." },
            { condition: { race: 'granocen' }, text: "Explain." },
            { condition: { race: 'vael' }, text: "The apology can wait. The reading cannot." },
            { condition: { race: 'thalassan' }, text: "We did not walk it back from the Vigil for the apology. Read." },
            { condition: { race: 'serath' }, text: "Begin where it begins." },
          ],
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          spriteKey: 'npc_scholar_aevryn',
          text: "The chamber under the Vigil is older than the order that tends it. The inscriptions are a list of places. Each is a site where what people call magic operates differently than ordinary ground. The Concord call what they do at theirs sun-channeling. The Serath call theirs Threshold work. My own order called the principle resonance. The Vigil tends one such site. I have decoded the locations of two others so far.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "The first is on the western end of the Long Horizon. The Dawn Watch sits on it now.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          spriteKey: 'npc_scholar_aevryn',
          text: "The Dawn Watch is a Vael holy site. The Faith of the Sun was founded there, and the Concord grew from the Faith. The site has been operated continuously since the inscriptions were carved, by Vael priests, then by the Faith, then by the Concord. I do not know what they do at it now, and the Faith do not invite outsiders to watch.",
        },
        {
          speaker: 'party',
          text: "And the second site?",
          variants: [
            { condition: { race: 'anthura' }, text: "Where is the second one?" },
            { condition: { race: 'caligari' }, text: "And the second." },
            { condition: { race: 'granocen' }, text: "Second." },
            { condition: { race: 'vael' }, text: "And the second site." },
            { condition: { race: 'thalassan' }, text: "Next on the list?" },
            { condition: { race: 'serath' }, text: "The second?" },
          ],
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          spriteKey: 'npc_scholar_aevryn',
          text: "In Verdanmere. At the foot of an ancient tree. The Anthura emerged at its base less than ten years ago. The site is on the same list as the Dawn Watch. I do not believe that is a coincidence.",
        },
        {
          speaker: 'npc',
          name: 'Vesper Thalane',
          spriteKey: 'npc_scholar_vesper',
          text: "Then the answers are at the tree, not at this desk. Whatever you write down, Aevryn, you will write down standing under it.",
        },
        {
          speaker: 'npc',
          name: 'Scholar Aevryn',
          spriteKey: 'npc_scholar_aevryn',
          text: "I cannot disagree. I have read everything in this room. If the Anthura emerged at the base of that tree because of what the Concord do at the western Long Horizon, I will not find it from paper in Irremar.",
        },
        {
          speaker: 'party',
          text: "Then we walk together.",
          variants: [
            { condition: { race: 'anthura' }, text: "Then we walk together. The tree is closer to me than this room is." },
            { condition: { race: 'caligari' }, text: "Then we walk together. Quietly." },
            { condition: { race: 'granocen' }, text: "Together, then." },
            { condition: { race: 'vael' }, text: "Then we walk together. Bring what you need." },
            { condition: { race: 'thalassan' }, text: "Then we walk together. South." },
            { condition: { race: 'serath' }, text: "Together." },
          ],
        },
        // TODO(2026-05-13): Crawl-hook ending pending a design pick.
        // Final 1-2 beats pivot from "we walk together" into the crawl.
      ],
    },
    // Debrief placeholder. Game voice, no NPC. Refines once crawl-hook ending
    // is locked.
    debrief: "The Sun-Sworn came in plain robes with Faith silver under them. They did not declare themselves. Aevryn finished his read while the party held the archive. Vesper has the inscriptions packed into her case. The convoy departs Irremar westward at first light, crossing the basin toward Motherroot.",
  },
};
